import SurfaceCard from './SurfaceCard';
import Eyebrow from './Eyebrow';

export interface MetricPoint {
  time: number;
  [containerId: string]: number;
}

/**
 * A metric as the artboards draw it: the name, the peak, the average, and a
 * row of bars for the shape of the run.
 *
 * Each row in `data` carries the reading of exactly one container — the series
 * builder pushes one row per sample, it does not merge them by timestamp — so
 * the bars bucket the whole range by time and average whatever falls in each
 * bucket. With one container that is the series itself; with several it is
 * their mean, which is the only reading that stays honest as containers come
 * and go mid-window.
 */
const BUCKETS = 24;

const bucketise = (data: MetricPoint[]) => {
  const samples = data
    .map(row => {
      const values = Object.entries(row)
        .filter(([key]) => key !== 'time')
        .map(([, value]) => value)
        .filter((value): value is number => typeof value === 'number');
      return values.length ? { time: row.time, value: values.reduce((a, b) => a + b, 0) / values.length } : null;
    })
    .filter((s): s is { time: number, value: number } => s !== null)
    .sort((a, b) => a.time - b.time);

  if (samples.length === 0) {
    return { bars: [] as number[], avg: 0, peak: 0, from: 0, to: 0 };
  }

  const from = samples[0].time;
  const to = samples[samples.length - 1].time;
  const span = Math.max(to - from, 1);

  const sums = new Array(BUCKETS).fill(0);
  const counts = new Array(BUCKETS).fill(0);
  samples.forEach(sample => {
    const index = Math.min(BUCKETS - 1, Math.floor(((sample.time - from) / span) * BUCKETS));
    sums[index] += sample.value;
    counts[index] += 1;
  });

  // Empty buckets stay at zero rather than being dropped: a gap in the series
  // is a fact about the run, and closing it up would draw a container that was
  // busy the whole time.
  const bars = sums.map((sum, i) => (counts[i] ? sum / counts[i] : 0));
  const total = samples.reduce((acc, s) => acc + s.value, 0);

  return {
    bars,
    avg: total / samples.length,
    peak: Math.max(...samples.map(s => s.value)),
    from,
    to,
  };
};

const clock = (ms: number) =>
  new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const MetricChart = ({ label, data, color, unit, format, formatPeak, isLoading, errorMessage, emptyLabel = 'No data for this window.' }: {
  label: React.ReactNode,
  data?: MetricPoint[],
  /** The bar colour, straight from the palette — one hue per metric. */
  color: string,
  unit: string,
  format: (value: number) => string,
  /** The peak reads on its own, away from the unit under the big value, so it
   * carries its own. Defaults to `format`. */
  formatPeak?: (value: number) => string,
  isLoading?: boolean,
  errorMessage?: string,
  emptyLabel?: string,
}) => {
  const { bars, avg, peak, from, to } = bucketise(data || []);
  // Having samples is what makes this a chart. A container that sat at zero
  // CPU for the whole window HAS data — reporting that as "no data" was the
  // difference between "idle" and "not measured".
  const hasData = bars.length > 0;

  return (
    <SurfaceCard className="p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <Eyebrow>{label}</Eyebrow>
        {hasData ? (
          <span className="font-courrier text-ds-115 text-navy-300">peak {(formatPeak || format)(peak)}</span>
        ) : null}
      </div>

      {isLoading ? (
        <div className="h-[72px] lg:h-[140px] flex items-center justify-center text-ds-135 text-navy-300">
          Loading…
        </div>
      ) : errorMessage ? (
        <div className="h-[72px] lg:h-[140px] flex items-center justify-center text-ds-135 text-state-err">
          {errorMessage}
        </div>
      ) : !hasData ? (
        <div className="h-[72px] lg:h-[140px] flex items-center justify-center text-ds-135 text-navy-300">
          {emptyLabel}
        </div>
      ) : (<>
        <div className="flex items-baseline gap-1.5">
          <span className="font-sora text-ds-20 font-semibold tracking-[-0.01em] text-white">
            {format(avg)}
          </span>
          <span className="text-ds-115 text-navy-200">{unit}</span>
        </div>

        <div className="flex items-end gap-[3px] h-[72px] lg:h-[140px] border-b border-navy-500">
          {bars.map((value, i) => (
            <div
              key={i}
              className="flex-1 min-w-[2px] rounded-t-[2px]"
              style={{
                // Floor of 2% so an idle sample still reads as a sample rather
                // than as a hole in the series — and so a series that is flat
                // at zero draws a baseline instead of nothing at all.
                height: `${peak > 0 ? Math.max(2, (value / peak) * 100) : 2}%`,
                background: color,
                opacity: 0.45 + (i / bars.length) * 0.55,
              }}
            />
          ))}
        </div>

        <div className="flex justify-between font-courrier text-ds-105 text-navy-300">
          <span>{clock(from)}</span>
          <span>{clock(from + (to - from) / 2)}</span>
          <span>{clock(to)}</span>
        </div>
      </>)}
    </SurfaceCard>
  );
};

export default MetricChart;
